import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, TextInput } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import {
  Plus,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  Home,
  Building2,
  Landmark,
} from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  CONTENT_MAX_WIDTH,
} from '../../../src/theme';
import { Header } from '@components/layout';
import { ErrorBanner, LoadingSpinner, useConfirm } from '@components/common';
import { useAuthStore } from '../../../src/store/authStore';
import { useAgentChecklistsStore } from '../../../src/store/agentChecklistsStore';
import { StakeholderType } from '../../../src/types';

type DraftItem = {
  id: string;
  label: string;
  description: string;
  // Carried through untouched. The editor exposes neither field, but rebuilding
  // items from the draft used to drop whatever it did not know about, so an
  // agent editing one label silently erased every item's category — and would
  // now erase the topic that carries observations across models.
  category?: string;
  topic?: string;
};

function generateId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function emptyItem(): DraftItem {
  return { id: generateId(), label: '', description: '' };
}

const STAKEHOLDER_OPTIONS: {
  value: StakeholderType;
  label: string;
  icon: typeof Home;
}[] = [
  { value: 'homeowner', label: 'Homeowner', icon: Home },
  { value: 'business', label: 'Business', icon: Building2 },
  { value: 'municipality', label: 'Municipality', icon: Landmark },
];

export default function ChecklistEditorScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const isCreate = id === 'new';
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user } = useAuthStore();
  const {
    checklists,
    isSaving,
    error,
    clearError,
    fetchChecklists,
    createChecklist,
    updateChecklist,
    deleteChecklist,
    countStructuresUsing,
  } = useAgentChecklistsStore();
  const confirm = useConfirm();

  const insurerName = user?.agentProfile?.insurerName ?? '';
  const existing = useMemo(
    () => (isCreate ? null : (checklists.find((c) => c.id === id) ?? null)),
    [isCreate, id, checklists]
  );

  const [stakeholderType, setStakeholderType] = useState<StakeholderType>(
    existing?.stakeholderType ?? 'homeowner'
  );
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>(existing?.serviceAreaIds ?? []);
  const [thresholds, setThresholds] = useState<number[]>(existing?.reportThresholds ?? [100]);
  const [items, setItems] = useState<DraftItem[]>(
    existing?.items.map((i) => ({
      id: i.id,
      label: i.label,
      description: i.description ?? '',
      category: i.category,
      topic: i.topic,
    })) ?? [emptyItem()]
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear any stale store error from a previous save attempt.
  useEffect(() => {
    clearError();
  }, [clearError]);

  // If we navigated to an existing checklist but the store is empty (deep link),
  // fetch checklists first.
  useEffect(() => {
    if (!isCreate && !existing && insurerName) {
      fetchChecklists(insurerName);
    }
  }, [isCreate, existing, insurerName, fetchChecklists]);

  // Sync state when existing loads after fetch.
  useEffect(() => {
    if (existing) {
      setStakeholderType(existing.stakeholderType);
      setSelectedAreaIds(existing.serviceAreaIds ?? []);
      setThresholds(existing.reportThresholds ?? [100]);
      setItems(
        existing.items.map((i) => ({
          id: i.id,
          label: i.label,
          description: i.description ?? '',
          category: i.category,
          topic: i.topic,
        }))
      );
    }
  }, [existing]);

  const canSave = items.some((i) => i.label.trim().length > 0);

  const handleSave = useCallback(async () => {
    const cleanedItems = items
      .filter((i) => i.label.trim().length > 0)
      .map((i) => ({
        id: i.id,
        label: i.label.trim(),
        ...(i.description.trim() ? { description: i.description.trim() } : {}),
        ...(i.category ? { category: i.category } : {}),
        ...(i.topic ? { topic: i.topic } : {}),
      }));

    if (cleanedItems.length === 0) return;

    try {
      if (isCreate) {
        await createChecklist(insurerName, {
          stakeholderType,
          serviceAreaIds: selectedAreaIds.length > 0 ? selectedAreaIds : undefined,
          items: cleanedItems,
          reportThresholds: thresholds,
        });
      } else {
        await updateChecklist(id!, {
          stakeholderType,
          serviceAreaIds: selectedAreaIds,
          items: cleanedItems,
          reportThresholds: thresholds,
        });
      }
      router.replace('/agent/checklists');
    } catch {
      // The store sets `error`; the ErrorBanner below surfaces it.
    }
  }, [
    items,
    isCreate,
    id,
    insurerName,
    stakeholderType,
    selectedAreaIds,
    thresholds,
    createChecklist,
    updateChecklist,
  ]);

  const handleDelete = async () => {
    if (!id || isCreate) return;
    setIsDeleting(true);

    try {
      const count = await countStructuresUsing(id);
      const message =
        count > 0
          ? `${count} structure${count === 1 ? '' : 's'} use this checklist. They will revert to the REDI baseline. Delete anyway?`
          : 'Delete this checklist? This cannot be undone.';

      const confirmed = await confirm({
        title: 'Delete checklist',
        message,
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!confirmed) {
        setIsDeleting(false);
        return;
      }
      await deleteChecklist(id);
      router.replace('/agent/checklists');
    } catch {
      setIsDeleting(false);
    }
  };

  // Copy this checklist's items/thresholds into a fresh draft. No API call —
  // the editor simply re-enters create mode with the fields pre-filled.
  const handleDuplicate = () => {
    router.replace({ pathname: '/agent/checklists/[id]', params: { id: 'new' } });
    setStakeholderType(existing?.stakeholderType ?? 'homeowner');
    setThresholds(existing?.reportThresholds ?? [100]);
    setItems(
      // A duplicate is a new model, so ids are fresh; the work each item
      // describes is unchanged, so category and topic come along.
      (existing?.items ?? []).map((i) => ({
        id: generateId(),
        label: i.label,
        description: i.description ?? '',
        category: i.category,
        topic: i.topic,
      }))
    );
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (itemId: string) => setItems((prev) => prev.filter((i) => i.id !== itemId));

  const updateItem = (itemId: string, field: keyof DraftItem, value: string) =>
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, [field]: value } : i)));

  const moveItem = (index: number, direction: -1 | 1) =>
    setItems((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const toggleThreshold = (value: number) =>
    setThresholds((prev) =>
      prev.includes(value)
        ? prev.filter((t) => t !== value)
        : [...prev, value].sort((a, b) => a - b)
    );

  if (!user?.agentProfile) return null;

  // Still loading existing checklist from deep link.
  if (!isCreate && !existing) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Edit Checklist" />
        <LoadingSpinner fullScreen message="Loading..." />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header
        showBack
        title={isCreate ? 'New Checklist' : 'Edit Checklist'}
        rightLabel={isSaving ? 'Saving...' : 'Save'}
        onRightPress={canSave && !isSaving ? handleSave : undefined}
      />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 80 }]}>
        <View style={styles.innerContent}>
          <ErrorBanner message={error} />
          {/* Stakeholder type */}
          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
            Stakeholder type
          </Text>
          <View style={styles.chipRow}>
            {STAKEHOLDER_OPTIONS.map((opt) => {
              const selected = opt.value === stakeholderType;
              const Icon = opt.icon;
              return (
                <Pressable
                  key={opt.value}
                  onPress={() => setStakeholderType(opt.value)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected }}
                  style={[
                    styles.chip,
                    {
                      borderColor: selected ? colors.primary : colors.border,
                      backgroundColor: selected ? colors.primarySoft : 'transparent',
                    },
                  ]}>
                  <Icon size={16} color={selected ? colors.primary : colors.textSecondary} />
                  <Text
                    style={[styles.chipLabel, { color: selected ? colors.primary : colors.text }]}>
                    {opt.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          {/* Report thresholds */}
          <Text
            style={[styles.sectionLabel, { color: colors.textSecondary, marginTop: SPACING.lg }]}>
            Auto-report at
          </Text>
          <View style={styles.chipRow}>
            {[25, 50, 75, 100].map((value) => {
              const selected = thresholds.includes(value);
              return (
                <Pressable
                  key={value}
                  onPress={() => toggleThreshold(value)}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: selected }}
                  style={[
                    styles.chip,
                    {
                      borderColor: selected ? colors.primary : colors.border,
                      backgroundColor: selected ? colors.primarySoft : 'transparent',
                    },
                  ]}>
                  <Text
                    style={[styles.chipLabel, { color: selected ? colors.primary : colors.text }]}>
                    {value}%
                  </Text>
                </Pressable>
              );
            })}
          </View>
          <Text style={[styles.hint, { color: colors.textTertiary }]}>
            A report is automatically emailed when progress reaches these levels.
          </Text>

          {/* Checklist items */}
          <Text
            style={[styles.sectionLabel, { color: colors.textSecondary, marginTop: SPACING.lg }]}>
            Checklist items
          </Text>
          {items.map((item, index) => (
            <View
              key={item.id}
              style={[
                styles.itemCard,
                { backgroundColor: colors.surface, borderColor: colors.border },
              ]}>
              <View style={styles.itemHeader}>
                <Pressable
                  hitSlop={8}
                  onPress={() => moveItem(index, -1)}
                  disabled={index === 0}
                  accessibilityLabel="Move item up"
                  style={[styles.moveBtn, index === 0 && styles.moveBtnDisabled]}>
                  <ChevronUp
                    size={16}
                    color={index === 0 ? colors.borderLight : colors.textSecondary}
                  />
                </Pressable>
                <Pressable
                  hitSlop={8}
                  onPress={() => moveItem(index, 1)}
                  disabled={index === items.length - 1}
                  accessibilityLabel="Move item down"
                  style={[styles.moveBtn, index === items.length - 1 && styles.moveBtnDisabled]}>
                  <ChevronDown
                    size={16}
                    color={index === items.length - 1 ? colors.borderLight : colors.textSecondary}
                  />
                </Pressable>
                <Text style={[styles.itemNumber, { color: colors.textTertiary }]}>{index + 1}</Text>
                <Pressable
                  hitSlop={8}
                  onPress={() => removeItem(item.id)}
                  accessibilityLabel="Remove item"
                  style={styles.removeBtn}>
                  <Trash2 size={16} color={colors.danger} />
                </Pressable>
              </View>
              <TextInput
                style={[styles.itemInput, { color: colors.text, borderColor: colors.border }]}
                placeholder="Item label (required)"
                placeholderTextColor={colors.textTertiary}
                value={item.label}
                onChangeText={(v) => updateItem(item.id, 'label', v)}
              />
              <TextInput
                style={[
                  styles.itemInput,
                  styles.itemInputSmall,
                  { color: colors.text, borderColor: colors.border },
                ]}
                placeholder="Description (optional)"
                placeholderTextColor={colors.textTertiary}
                value={item.description}
                onChangeText={(v) => updateItem(item.id, 'description', v)}
                multiline
              />
            </View>
          ))}
          <Pressable
            style={[styles.addItemBtn, { borderColor: colors.border }]}
            onPress={addItem}
            accessibilityRole="button"
            accessibilityLabel="Add checklist item">
            <Plus size={18} color={colors.primary} />
            <Text style={[styles.addItemLabel, { color: colors.primary }]}>Add item</Text>
          </Pressable>

          {/* Duplicate + delete (edit mode only) */}
          {!isCreate && (
            <>
              <Pressable
                style={[styles.deleteBtn, { borderColor: colors.border, marginTop: SPACING.xl }]}
                onPress={handleDuplicate}
                accessibilityRole="button"
                accessibilityLabel="Duplicate this checklist">
                <Copy size={16} color={colors.textSecondary} />
                <Text style={[styles.deleteBtnLabel, { color: colors.textSecondary }]}>
                  Duplicate checklist
                </Text>
              </Pressable>
              <Pressable
                style={[styles.deleteBtn, { borderColor: colors.danger, marginTop: SPACING.sm }]}
                onPress={handleDelete}
                disabled={isDeleting}
                accessibilityRole="button"
                accessibilityLabel="Delete this checklist">
                <Trash2 size={16} color={colors.danger} />
                <Text style={[styles.deleteBtnLabel, { color: colors.danger }]}>
                  {isDeleting ? 'Deleting...' : 'Delete checklist'}
                </Text>
              </Pressable>
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
  },
  innerContent: {
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
    alignSelf: 'center',
    padding: SPACING.md,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.sm,
  },
  chipRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
    flexWrap: 'wrap',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1.5,
    gap: SPACING.sm,
  },
  chipLabel: {
    ...TYPOGRAPHY.button,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 4,
  },
  itemCard: {
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: SPACING.sm,
    gap: SPACING.sm,
  },
  itemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  moveBtn: {
    padding: 2,
  },
  moveBtnDisabled: {
    opacity: 0.4,
  },
  itemNumber: {
    ...TYPOGRAPHY.label,
    flex: 1,
  },
  removeBtn: {
    padding: 4,
  },
  itemInput: {
    ...TYPOGRAPHY.body,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
  },
  itemInputSmall: {
    ...TYPOGRAPHY.bodySmall,
    minHeight: 40,
  },
  addItemBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.xs,
    paddingVertical: SPACING.md,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderStyle: 'dashed',
    marginTop: SPACING.xs,
  },
  addItemLabel: {
    ...TYPOGRAPHY.button,
  },
  deleteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.md,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    marginTop: SPACING.xl,
  },
  deleteBtnLabel: {
    ...TYPOGRAPHY.button,
  },
});
